import React from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import { Close, FileCopy } from "@material-ui/icons";
import PerfectScrollbar from "react-perfect-scrollbar";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useTranslation } from "react-i18next";

/**
 * Componente centralizado para modals de AI Insights
 * Mantém exatamente o mesmo estilo e funcionalidades dos modals originais
 */
const AIInsightsModal = ({
  // Props básicas do modal
  open,
  onClose,
  // Props de conteúdo
  title, // chave de tradução para o título
  disclaimer, // chave de tradução para o disclaimer
  content, // conteúdo markdown a ser exibido
  // Props de loading
  isLoading,
  loadingComponent, // componente de loading customizado
  // Props de erro
  error,
  // Props de copy
  onCopy,
  isCopying,
  copySuccessKey = "copied_success", // chave de tradução para sucesso na cópia
  copyTooltipKey = "copy_to_clipboard", // chave de tradução para tooltip de cópia
  // Props de estilo
  userDetails,
  // Props de markdown (para permitir customização se necessário)
  markdownComponents = null,
  // Props de fallback
  noContentKey = "no_content",
  errorLabelKey = "general.error",
  closeLabelKey = "general.close",
}) => {
  const { t } = useTranslation();

  // Componentes padrão do ReactMarkdown (baseado nos originais)
  const defaultMarkdownComponents = {
    h1: ({ children }) => (
      <Typography
        variant="h6"
        style={{ fontWeight: "bold", marginBottom: "12px", marginTop: "16px" }}
      >
        {children}
      </Typography>
    ),
    h2: ({ children }) => (
      <Typography
        variant="subtitle1"
        style={{ fontWeight: "bold", marginBottom: "10px", marginTop: "14px" }}
      >
        {children}
      </Typography>
    ),
    h3: ({ children }) => (
      <Typography
        variant="subtitle2"
        style={{ fontWeight: "bold", marginBottom: "8px", marginTop: "12px" }}
      >
        {children}
      </Typography>
    ),
    h4: ({ children }) => (
      <Typography
        variant="body1"
        style={{ fontWeight: "bold", marginBottom: "6px", marginTop: "10px" }}
      >
        {children}
      </Typography>
    ),
    p: ({ children }) => (
      <Typography
        variant="body2"
        style={{ margin: 0, marginBottom: "12px", color: "#333" }}
      >
        {children}
      </Typography>
    ),
    strong: ({ children }) => (
      <strong style={{ fontWeight: 600, color: "#333" }}>{children}</strong>
    ),
    li: ({ children }) => (
      <li style={{ marginBottom: "6px", marginLeft: "16px", color: "#333" }}>
        {children}
      </li>
    ),
    ul: ({ children }) => (
      <ul style={{ marginBottom: "12px", paddingLeft: "16px" }}>{children}</ul>
    ),
    ol: ({ children }) => (
      <ol style={{ marginBottom: "12px", paddingLeft: "16px" }}>{children}</ol>
    ),
  };

  // Usar componentes customizados se fornecidos, senão usar os padrão
  const finalMarkdownComponents =
    markdownComponents || defaultMarkdownComponents;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      <DialogTitle
        style={{
          backgroundColor: "#f5f5f5",
          color: "#333",
          borderBottom: `2px solid ${
            userDetails?.implementation_color || "#1976d2"
          }`,
          paddingBottom: "12px",
          position: "relative",
          paddingRight: "100px", // Espaço para os dois botões
        }}
      >
        <Typography
          variant="h6"
          style={{ fontSize: "1.1rem", fontWeight: 500 }}
        >
          {t(title)}
        </Typography>
        <Typography
          variant="caption"
          style={{
            color: "#666",
            fontStyle: "italic",
            fontSize: "0.75rem",
            display: "block",
            marginTop: "4px",
          }}
        >
          {t(disclaimer)}
        </Typography>

        {/* Botões no header - Copy e Close lado a lado */}
        <div
          style={{
            position: "absolute",
            right: 8,
            top: 8,
            display: "flex",
            gap: 4,
          }}
        >
          <Tooltip
            title={isCopying ? t(copySuccessKey) : t(copyTooltipKey)}
            disablePortal={false}
            style={{ zIndex: 10001 }}
          >
            <IconButton
              onClick={onCopy}
              disabled={!content || isLoading || isCopying}
              style={{
                color: "#424242",
                padding: "8px",
                cursor:
                  !content || isLoading || isCopying
                    ? "not-allowed"
                    : "pointer",
                opacity: !content || isLoading || isCopying ? 0.5 : 1,
                backgroundColor: "transparent",
                "&:hover": {
                  color: userDetails?.implementation_color || "#1976d2",
                  backgroundColor: "transparent",
                },
              }}
              onMouseEnter={(e) => {
                if (!(!content || isLoading || isCopying)) {
                  e.currentTarget.style.color =
                    userDetails?.implementation_color || "#1976d2";
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = "#424242";
              }}
            >
              <FileCopy style={{ fontSize: "20px" }} />
            </IconButton>
          </Tooltip>

          <Tooltip
            title={t(closeLabelKey)}
            disablePortal={false}
            style={{ zIndex: 10001 }}
          >
            <IconButton
              onClick={onClose}
              style={{
                color: "#424242",
                padding: "8px",
                cursor: "pointer",
                backgroundColor: "transparent",
                "&:hover": {
                  color: userDetails?.implementation_color || "#1976d2",
                  backgroundColor: "transparent",
                },
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color =
                  userDetails?.implementation_color || "#1976d2";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = "#424242";
              }}
            >
              <Close style={{ fontSize: "20px" }} />
            </IconButton>
          </Tooltip>
        </div>
      </DialogTitle>
      <DialogContent style={{ minHeight: 400, padding: 0 }}>
        <PerfectScrollbar
          style={{
            maxHeight: "70vh",
            padding: isLoading ? "0" : "24px",
          }}
        >
          {isLoading ? (
            // Se um loadingComponent for fornecido, usar ele, senão usar um padrão
            loadingComponent || (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "300px",
                }}
              >
                <Typography>Loading...</Typography>
              </div>
            )
          ) : error ? (
            <div
              style={{
                padding: "24px",
                textAlign: "center",
                color: "#f44336",
              }}
            >
              <Typography variant="body1">
                <strong>{t(errorLabelKey)}:</strong> {error}
              </Typography>
            </div>
          ) : (
            <div style={{ lineHeight: 1.6 }}>
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={finalMarkdownComponents}
              >
                {content || t(noContentKey)}
              </ReactMarkdown>
            </div>
          )}
        </PerfectScrollbar>
      </DialogContent>
    </Dialog>
  );
};

AIInsightsModal.propTypes = {
  // Props básicas do modal
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  // Props de conteúdo
  title: PropTypes.string.isRequired,
  disclaimer: PropTypes.string.isRequired,
  content: PropTypes.string,
  // Props de loading
  isLoading: PropTypes.bool,
  loadingComponent: PropTypes.node,
  // Props de erro
  error: PropTypes.string,
  // Props de copy
  onCopy: PropTypes.func,
  isCopying: PropTypes.bool,
  copySuccessKey: PropTypes.string,
  copyTooltipKey: PropTypes.string,
  // Props de estilo
  userDetails: PropTypes.shape({
    implementation_color: PropTypes.string,
  }),
  // Props de markdown
  markdownComponents: PropTypes.object,
  // Props de fallback
  noContentKey: PropTypes.string,
  errorLabelKey: PropTypes.string,
  closeLabelKey: PropTypes.string,
};

AIInsightsModal.defaultProps = {
  content: "",
  isLoading: false,
  loadingComponent: null,
  error: null,
  onCopy: () => {},
  isCopying: false,
  copySuccessKey: "copied_success",
  copyTooltipKey: "copy_to_clipboard",
  userDetails: null,
  markdownComponents: null,
  noContentKey: "no_content",
  errorLabelKey: "general.error",
  closeLabelKey: "general.close",
};

export default AIInsightsModal;
